import { Simulation } from '../core/simulation/Simulation';
import { GameLoop } from './GameLoop';
import type { Renderer } from '../renderers/base/Renderer';
import { RendererFactory } from '../renderers/RendererFactory';
import { InputManager } from '../input/InputManager';
import { UIManager } from '../ui/UIManager';
import type { SimulationConfig } from '../core/config/SimulationConfig';
import type { RendererType } from '../core/types/SimulationTypes';
import { DEFAULT_CONFIG } from '../core/config/SimulationConfig';

export class SceneManager {
  private simulation: Simulation;
  private renderer: Renderer;
  private currentRendererType: RendererType = 'voxel';
  private inputManager: InputManager;
  private uiManager: UIManager | null = null;
  private gameLoop: GameLoop;
  private container: HTMLElement;

  constructor(container: HTMLElement, config: SimulationConfig = DEFAULT_CONFIG) {
    this.container = container;
    this.simulation = new Simulation(config);
    this.renderer = RendererFactory.create('voxel');
    this.inputManager = new InputManager(container);
    this.gameLoop = new GameLoop(config.physics.updateRate);
  }

  public initialize(): void {
    this.renderer.initialize(this.container);

    const sidebar = document.getElementById('sidebar');
    if (sidebar) {
      this.uiManager = new UIManager(
        sidebar,
        this.simulation,
        this.inputManager,
        this.gameLoop
      );
      this.uiManager.setSceneManager(this);
      this.uiManager.initialize();
    }

    this.gameLoop.start(
      (deltaTime) => this.updatePhysics(deltaTime),
      (deltaTime) => this.render(deltaTime)
    );
  }

  private updatePhysics(deltaTime: number): void {
    this.inputManager.update(this.simulation, this.renderer);
    this.simulation.update(deltaTime);
  }

  private render(deltaTime: number): void {
    const state = this.simulation.getState();
    this.renderer.render(state);

    // Draw hover preview
    const hoverCoord = this.inputManager.getHoverCoordinate();
    if (hoverCoord) {
      const brushSize = this.inputManager.getCurrentBrushSize();
      this.renderer.drawHoverPreview(hoverCoord, brushSize);
    }
  }

  public getSimulation(): Simulation {
    return this.simulation;
  }

  public getInputManager(): InputManager {
    return this.inputManager;
  }

  public getGameLoop(): GameLoop {
    return this.gameLoop;
  }

  public getRenderer(): Renderer {
    return this.renderer;
  }

  public pause(): void {
    if (this.gameLoop.isActive()) {
      this.gameLoop.stop();
    }
  }

  public resume(): void {
    if (!this.gameLoop.isActive()) {
      this.gameLoop.start(
        (deltaTime) => this.updatePhysics(deltaTime),
        (deltaTime) => this.render(deltaTime)
      );
    }
  }

  public isPaused(): boolean {
    return !this.gameLoop.isActive();
  }

  public switchRenderer(type: RendererType): void {
    if (type === this.currentRendererType) return;

    const wasPaused = this.isPaused();

    // Always stop the loop before switching
    this.gameLoop.stop();

    this.renderer.dispose();
    this.renderer = RendererFactory.create(type);
    this.renderer.initialize(this.container);
    this.currentRendererType = type;

    // Restart the loop if it was running
    if (!wasPaused) {
      this.gameLoop.start(
        (deltaTime) => this.updatePhysics(deltaTime),
        (deltaTime) => this.render(deltaTime)
      );
    }
  }

  public getCurrentRendererType(): RendererType {
    return this.currentRendererType;
  }

  public dispose(): void {
    this.gameLoop.stop();
    this.renderer.dispose();
    this.inputManager.dispose();
  }
}
